import * as React from 'react';
import { resolve } from 'inversify-react';
import { observer } from 'mobx-react';
import autobind from 'autobind-decorator';

import * as Permissions from 'src/app/permissions';
import { CardController } from 'src/app/shared/card.controller';
import { BillingService } from 'src/app/accounting/shared/billing.service';
import { ObjectUtils } from 'src/core/utils/object';
import { ToastNotifications } from 'src/core/ui/toast';
import { DataLocalService } from 'src/core/services/data-local.service';
import { QuotePaymentList, QuotePaymentPermission } from 'src/app/accounting/shared/payments/quote-payment-list';
import { IdentityService } from 'src/core/services/identity.service';
import { TargetModel } from '../quotes/parts/payment-modal';

interface Props extends ComponentViewProps<QuoteItem> {
	mode: ControlMode;
	currencyId: number;
	currencyCode: string;
	paxDataSet: DataSet<QuotePaxItem>;
	paymentDataSet: DataSet<PaymentSummary>;
	onSubmitPayment: (paymentInfo: PaymentInfo, immediatelyPerformPayment: boolean) => Promise<void>;
	reloadQuote: () => Promise<void>;
}

@observer
export class QuotePaymentListController extends React.Component<Props, {}> {
	@resolve
	private billingService: BillingService;

	@resolve
	private identityService: IdentityService;

	@resolve
	private toast: ToastNotifications;

	private async performAsync<T>(body: () => Promise<T>): Promise<T> {
		const { setBusy } = this.props;

		setBusy(true);
		try {
			const result = await body();
			return result;
		} catch (error) {
			this.toast.showToast('Request failed', ObjectUtils.parseError(error), 'error');
		} finally {
			setBusy(false);
		}
	}

	@autobind
	private async handleSubmitPayment(paymentInfo: PaymentInfo, immediatelyPerformPayment: boolean): Promise<void> {
		return this.performAsync(async () => {
			await this.props.onSubmitPayment(paymentInfo, immediatelyPerformPayment);
		});
	}

	@autobind
	private async handleSubmitRefund(paymentInfo: PaymentInfo, immediatelyPerformPayment: boolean, targetPaymentId?: number): Promise<void> {
		this.performAsync(async () => {
			const { reloadQuote, paymentDataSet } = this.props;

			await this.billingService.registerRefund(paymentInfo, immediatelyPerformPayment, targetPaymentId);
			await paymentDataSet.reload();
			await reloadQuote();
		});
	}

	@autobind
	private async handlePerformPayment(payment: PaymentSummary): Promise<void> {
		this.performAsync(async () => {
			const { reloadQuote, paymentDataSet } = this.props;

			await this.billingService.performPayment(payment.id);
			await paymentDataSet.reload();
			await reloadQuote();
		});
	}

	render(): React.ReactNode {
		const { setBusy, mode, currencyId, currencyCode, model, paymentDataSet, paxDataSet } = this.props;
		if (paymentDataSet == null) {
			return null;
		}

		const permissions: QuotePaymentPermission[] = [];
		if (Permissions.paymentWritePermission.every(m => this.identityService.isAuthorized(m))) {
			permissions.push('register-payment', 'register-refund', 'perform-payment');
		}

		let type: 'client' | 'agent';
		let payments: DataSet<PaymentSummary>;
		let targetOptions: TargetModel[];
		if (model.paymentMode === 0) {
			type = 'client';
			payments = new DataLocalService<PaymentSummary>(paymentDataSet.items.filter(i => i.accountingTarget & 1));
			targetOptions = paxDataSet.items
				.filter(m => m.dynamicProperties.billingTo === m.clientId)
				.map(m => ({
					id: m.clientId,
					firstName: (m.dynamicProperties['foreName'] + ' ' + (m.dynamicProperties['middleName'] || '')).trim(),
					lastName: m.dynamicProperties['surName'],
					displayName: m.clientDisplayName,
				}));
		} else if (model.agentId != null) {
			type = 'agent';
			payments = new DataLocalService<PaymentSummary>(paymentDataSet.items.filter(i => i.accountingTarget & 2));
			targetOptions = [{
				id: model.agentId,
				firstName: model.agentName,
				lastName: model.agentName,
				displayName: model.agentName,
			}];
		}

		return (
			<CardController
				mode={mode}
				className="card-borderless">
				<QuotePaymentList
					mode={mode}
					type={type}
					model={payments}
					defaultCurrencyId={currencyId}
					defaultCurrencyCode={currencyCode}
					quote={model}
					targetOptions={targetOptions}
					onPerformPayment={this.handlePerformPayment}
					onSubmitPayment={this.handleSubmitPayment}
					onSubmitRefund={this.handleSubmitRefund}
					permissions={permissions}
					setBusy={setBusy}
				/>
			</CardController>
		);
	}
}



// WEBPACK FOOTER //
// ./src/app/bookings/quote-payments/quote-payment.controller.tsx